import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/shared/ui/toaster";
import { AuthProvider } from "@/features/auth/model/AuthContext";
import { Permission } from "@/shared/types/roles";

// Pages
import { PackGrid } from "@/features/packs/pages/PackGrid";
import Auth from "@/features/auth/pages/Login";
import { ForgotPassword } from "@/features/auth/pages/ForgotPassword";
import ResetPassword from "@/features/auth/pages/ResetPassword";
import Users from "@/features/profiles/pages/Users";
import UserEdit from "@/features/profiles/pages/UserEdit";
import NotFound from "@/shared/widgets/NotFound";
import PackForm from "@/features/packs/pages/PackForm";
import PresetForm from "@/features/presets/pages/PresetForm";
import { Unauthorized } from "@/shared/widgets/Unauthorized";
import DataDeletionPage from "@/features/lgpd/pages/DataDeletionPage";

// Components
import { ProtectedRoute } from "@/shared/widgets/auth/ProtectedRoute";
import { AppLayout } from "@/shared/widgets/layout/AppLayout";

// Features
import AnalyticsPage from "@/features/analytics/pages/AnalyticsPage";
import AppAnalyticsPage from "@/features/app-analytics/pages/AppAnalyticsPage";
import Tags from "@/features/tags/pages/index";
import TagCreateUpdate from "@/features/tags/pages/create-update";
import LearningsIndex from "@/features/learnings/pages/LearningIndex";
import LearningEdit from "@/features/learnings/pages/LearningEdit";
import CategoriesIndex from "@/features/categories/pages/CategoriesIndex";
import CategoryEdit from "@/features/categories/pages/CategoryEdit";
import { ContractsList, ContractForm } from "@/features/contracts/pages";
import HighlightManagementPage from "@/features/highlights/pages/HighlightManagementPage";
import { ReceivableForm, ReceivablesList } from "@/features/receivables/pages";
import { ReceivableShareConfigList, ReceivableShareConfigForm } from "@/features/receivable-share-configs/pages";
import { PaymentsList, PaymentForm } from "@/features/payments/pages";
import { ReceivableSnapshotsList } from "@/features/receivable-snapshots/pages";

function App() {
  // Move queryClient inside the component
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  });

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter basename={import.meta.env.BASE_URL || "/"}>
          <Routes>
            {/* Rotas públicas (sem sidebar) */}
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/forgot-password" element={<ForgotPassword />} />
            <Route path="/auth/reset-password" element={<ResetPassword />} />
            <Route path="/exclusao-dados" element={<DataDeletionPage />} />
            <Route path="/unauthorized" element={<Unauthorized />} />

            {/* Rotas protegidas (com sidebar) */}
            <Route
              path="/"
              element={
                <ProtectedRoute permissions={[Permission.PACKS_VIEW]}>
                  <AppLayout>
                    <PackGrid />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/stats"
              element={
                <ProtectedRoute permissions={[Permission.ANALYTICS_VIEW]}>
                  <AppLayout>
                    <AnalyticsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/app-analytics"
              element={
                <ProtectedRoute permissions={[Permission.ANALYTICS_VIEW]}>
                  <AppLayout>
                    <AppAnalyticsPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute permissions={[Permission.USERS_VIEW]}>
                  <AppLayout>
                    <Users />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users/:id/edit"
              element={
                <ProtectedRoute permissions={[Permission.USERS_EDIT]}>
                  <AppLayout>
                    <UserEdit />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/contracts"
              element={
                <ProtectedRoute permissions={[Permission.CONTRACTS_VIEW]}>
                  <AppLayout>
                    <ContractsList />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/contracts/new"
              element={
                <ProtectedRoute permissions={[Permission.CONTRACTS_EDIT]}>
                  <AppLayout>
                    <ContractForm />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/contracts/:id"
              element={
                <ProtectedRoute permissions={[Permission.CONTRACTS_EDIT]}>
                  <AppLayout>
                    <ContractForm />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/packs/new"
              element={
                <ProtectedRoute permissions={[Permission.PACKS_CREATE]}>
                  <AppLayout>
                    <PackForm />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/packs/:id"
              element={
                <ProtectedRoute permissions={[Permission.PACKS_VIEW]}>
                  <AppLayout>
                    <PackForm />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/presets/new"
              element={
                <ProtectedRoute permissions={[Permission.PRESETS_EDIT]}>
                  <AppLayout>
                    <PresetForm />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/presets/:id"
              element={
                <ProtectedRoute permissions={[Permission.PRESETS_EDIT]}>
                  <AppLayout>
                    <PresetForm />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tags"
              element={
                <ProtectedRoute permissions={[Permission.TAGS_VIEW]}>
                  <AppLayout>
                    <Tags />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tags/new"
              element={
                <ProtectedRoute permissions={[Permission.TAGS_CREATE]}>
                  <AppLayout>
                    <TagCreateUpdate />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tags/edit/:id"
              element={
                <ProtectedRoute permissions={[Permission.TAGS_EDIT]}>
                  <AppLayout>
                    <TagCreateUpdate />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/learning"
              element={
                <ProtectedRoute permissions={[Permission.LEARNING_VIEW]}>
                  <AppLayout>
                    <LearningsIndex />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/learning/new"
              element={
                <ProtectedRoute permissions={[Permission.LEARNING_EDIT]}>
                  <AppLayout>
                    <LearningEdit />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/learning/edit/:id"
              element={
                <ProtectedRoute permissions={[Permission.LEARNING_EDIT]}>
                  <AppLayout>
                    <LearningEdit />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/categories"
              element={
                <ProtectedRoute permissions={[Permission.CATEGORIES_VIEW]}>
                  <AppLayout>
                    <CategoriesIndex />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/categories/new"
              element={
                <ProtectedRoute permissions={[Permission.CATEGORIES_EDIT]}>
                  <AppLayout>
                    <CategoryEdit />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/categories/edit/:id"
              element={
                <ProtectedRoute permissions={[Permission.CATEGORIES_EDIT]}>
                  <AppLayout>
                    <CategoryEdit />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/highlights"
              element={
                <ProtectedRoute permissions={[Permission.HIGHLIGHTS_VIEW]}>
                  <AppLayout>
                    <HighlightManagementPage />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/receivables/"

            >
              <Route path="" element={
                <ProtectedRoute permissions={[Permission.RECEIVABLES_VIEW]}>
                  <AppLayout>
                    <ReceivablesList />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path="new" element={
                <ProtectedRoute permissions={[Permission.RECEIVABLES_EDIT]}>
                  <AppLayout>
                    <ReceivableForm />
                  </AppLayout>
                </ProtectedRoute>
              } />
              <Route path=":id" element={
                <ProtectedRoute permissions={[Permission.RECEIVABLES_EDIT]}>
                  <AppLayout>
                    <ReceivableForm />
                  </AppLayout>
                </ProtectedRoute>
              } />
            </Route>
            <Route path="/receivable-share-configs/">
              <Route
                path=""
                element={
                  <ProtectedRoute permissions={[Permission.RECEIVABLES_VIEW]}>
                    <AppLayout>
                      <ReceivableShareConfigList />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="new"
                element={
                  <ProtectedRoute permissions={[Permission.RECEIVABLES_EDIT]}>
                    <AppLayout>
                      <ReceivableShareConfigForm />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path=":id"
                element={
                  <ProtectedRoute permissions={[Permission.RECEIVABLES_EDIT]}>
                    <AppLayout>
                      <ReceivableShareConfigForm />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route
              path="/receivable-snapshots"
              element={
                <ProtectedRoute permissions={[Permission.RECEIVABLES_VIEW]}>
                  <AppLayout>
                    <ReceivableSnapshotsList />
                  </AppLayout>
                </ProtectedRoute>
              }
            />
            <Route path="/payments/">
              <Route
                path=""
                element={
                  <ProtectedRoute permissions={[Permission.PAYMENTS_VIEW]}>
                    <AppLayout>
                      <PaymentsList />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="new"
                element={
                  <ProtectedRoute permissions={[Permission.PAYMENTS_CREATE, Permission.PAYMENTS_EDIT]}>
                    <AppLayout>
                      <PaymentForm />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path=":id"
                element={
                  <ProtectedRoute permissions={[Permission.PAYMENTS_EDIT]}>
                    <AppLayout>
                      <PaymentForm />
                    </AppLayout>
                  </ProtectedRoute>
                }
              />
            </Route>
            {/* Rota 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <Toaster />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider >
  );
}

export default App;
